import React from 'react';

import { MultiResizedImagesDTO } from '@/data/dtos/Events/MultiResizedImagesDTO';

interface MultiImagePictureProps {
    images: MultiResizedImagesDTO;
    alt: string;
    className?: string;
    width?: number;
    height?: number;
    sizes?: string;
    openFull?: boolean;
    lazy?: boolean;
    priority?: boolean;
    style?: React.CSSProperties;
}

export default function MultiImagePicture({ 
    images, 
    alt, 
    className = '', 
    width, 
    height,
    sizes,
    openFull = false,
    lazy = true,
    priority = false,
    style
}: MultiImagePictureProps) {

    if (!images || images.length === 0) {
        return null;
    }


    const sortedImages = [...images].sort((a, b) => a.width - b.width);
    const largestImage = sortedImages[sortedImages.length - 1];
    
    // If sizes is not provided, calculate it based on the context
    const calculatedSizes = sizes || (() => {
        // If width is provided, use it as the exact size
        if (width) {
            return `${width}px`;
        }
        
        // For avatars (detected by class name)
        if (className.includes('avatar')) {
            return '100px';
        }
        
        // Default responsive sizes based on available image widths
        const maxWidth = Math.max(...sortedImages.map(img => img.width));
        const minWidth = Math.min(...sortedImages.map(img => img.width));
        
        return `(max-width: ${minWidth}px) ${minWidth}px, ${maxWidth}px`;
    })();

    // Create srcSet string for all images
    const srcSet = sortedImages
        .map(img => `${img.src} ${img.width}w`)
        .join(', ');

    // Check if all images are SVG
    const isSvg = sortedImages.every(img => img.type === 'image/svg+xml');
    
    const pictureElement = (
        <picture>
            {/* Add source elements for better browser optimization */}
            {!isSvg && sortedImages.length > 1 && (
                <source
                    srcSet={srcSet}
                    sizes={calculatedSizes}
                    type={sortedImages[0].type}
                />
            )}
            <img
                src={sortedImages[0].src}
                srcSet={isSvg ? undefined : srcSet}
                sizes={isSvg ? undefined : calculatedSizes}
                className={className}
                alt={alt}
                width={width}
                height={height}
                loading={lazy ? 'lazy' : 'eager'}
                fetchPriority={priority ? 'high' : 'auto'}
                style={{
                    ...style,
                    ...(openFull ? { cursor: 'pointer' } : {})
                }}
                decoding="async"
            />
        </picture>
    );

    if (openFull) {
        return (
            <a 
                href={largestImage.src} 
                target="_blank" 
                rel="noopener noreferrer"
                title={`Open ${alt} in full size`}
            >
                {pictureElement}
            </a>
        );
    }

    return pictureElement;
} 